import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BaseAPI } from 'src/providers/base-api';
import type { CodeTable, CodeValue } from 'src/types/codeTable';

export interface CodeTableState {
  byTypeCode: Record<string, CodeValue[]>;
  loadedTypeCodes: string[];           // Track which codes are loaded
  loading: boolean;
  loadingTypeCodes: string[];          // Track individual loading states
  error: string | null;
  lastFetched: number | null;
  isFullyLoaded: boolean;              // All code tables loaded
}

const initialState: CodeTableState = {
  byTypeCode: {},
  loadedTypeCodes: [],
  loading: false,
  loadingTypeCodes: [],
  error: null,
  lastFetched: null,
  isFullyLoaded: false,
};

/**
 * Transform raw API response to minimal structure
 */
const transformCodeTable = (table: any): CodeTable => ({
  typeCode: table.typeCode,
  values: (table.values || []).map((v: any) => ({
    id: v.id,
    name: v.name,
    typeCode: v.typeCode || v.typecode || v.codeRef,
    codeRef: v.codeRef || v.typeCode || v.typecode
  }))
});

/**
 * Fetch all code tables (initial load)
 */
export const fetchAllCodeTables = createAsyncThunk(
  'codeTables/fetchAll',
  async (_, { getState, rejectWithValue }) => {
  const state = (getState() as { codeTables: CodeTableState }).codeTables;

  // Skip if already loaded
  if (state.isFullyLoaded) {
    return null;
  }

  try {
    return new Promise<CodeTable[]>((resolve, reject) => {
      BaseAPI.getCodeReference((result: any) => {
        if (result.status === 200) {
          const codeTables: CodeTable[] = (result.data.codeTables || []).map(transformCodeTable);
          resolve(codeTables);
        } else {
          reject(new Error(result.message || 'Failed to fetch code tables'));
        }
      });
    });
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
  }
);

/**
 * Force refresh all code tables (bypasses cache)
 */
export const refreshCodeTables = createAsyncThunk(
  'codeTables/refresh',
  async (_, { rejectWithValue }) => {
  try {
    return new Promise<CodeTable[]>((resolve, reject) => {
      BaseAPI.getCodeReference((result: any) => {
        if (result.status === 200) {
          const codeTables: CodeTable[] = (result.data.codeTables || []).map(transformCodeTable);
          resolve(codeTables);
        } else {
          reject(new Error(result.message || 'Failed to fetch code tables'));
        }
      });
    });
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
  }
);

/**
 * Fetch single code table by typeCode (lazy load)
 * For future use when code tables become heavy
 */
export const fetchCodeTableByType = createAsyncThunk(
  'codeTables/fetchByType',
  async (typeCode: string, { getState, rejectWithValue }) => {
  const state = (getState() as { codeTables: CodeTableState }).codeTables;

  // Skip if already loaded
  if (state.byTypeCode[typeCode]) {
    return null;
  }

  try {
    return new Promise<{ typeCode: string; values: CodeValue[] }>((resolve, reject) => {
      // Future: BaseAPI.getCodeTableByType(typeCode, callback)
      // For now, fall back to full fetch and extract the needed type
      BaseAPI.getCodeReference((result: any) => {
        if (result.status === 200) {
            const table = result.data.codeTables?.find(
              (t: any) => t.typeCode === typeCode
            );
          if (table) {
            resolve({
              typeCode: table.typeCode,
              values: (table.values || []).map((v: any) => ({
                id: v.id,
                name: v.name,
                  typeCode: v.typeCode || v.typecode,
                })),
            });
          } else {
            reject(new Error(`Code table '${typeCode}' not found`));
          }
        } else {
          reject(new Error(result.message || 'Failed to fetch code table'));
        }
      });
    });
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
  }
);

const codeTableSlice = createSlice({
  name: 'codeTables',
  initialState,
  reducers: {
    clearCodeTables: (state) => {
      state.byTypeCode = {};
      state.loadedTypeCodes = [];
      state.isFullyLoaded = false;
      state.lastFetched = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllCodeTables.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCodeTables.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          action.payload.forEach((table) => {
            state.byTypeCode[table.typeCode] = table.values;
            if (!state.loadedTypeCodes.includes(table.typeCode)) {
              state.loadedTypeCodes.push(table.typeCode);
            }
          });
          state.isFullyLoaded = true;
          state.lastFetched = Date.now();
        }
      })
      .addCase(fetchAllCodeTables.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || action.error.message || 'Unknown error';
      })

      // Refresh
      .addCase(refreshCodeTables.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshCodeTables.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.byTypeCode = {};
          state.loadedTypeCodes = [];
          action.payload.forEach((table) => {
            state.byTypeCode[table.typeCode] = table.values;
            state.loadedTypeCodes.push(table.typeCode);
          });
          state.isFullyLoaded = true;
          state.lastFetched = Date.now();
        }
      })
      .addCase(refreshCodeTables.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || action.error.message || 'Unknown error';
      })

      // Fetch Single
      .addCase(fetchCodeTableByType.pending, (state, action) => {
        if (!state.loadingTypeCodes.includes(action.meta.arg)) {
          state.loadingTypeCodes.push(action.meta.arg);
        }
      })
      .addCase(fetchCodeTableByType.fulfilled, (state, action) => {
        const typeCode = action.meta.arg;
        state.loadingTypeCodes = state.loadingTypeCodes.filter(t => t !== typeCode);
        if (action.payload) {
          state.byTypeCode[action.payload.typeCode] = action.payload.values;
          if (!state.loadedTypeCodes.includes(action.payload.typeCode)) {
            state.loadedTypeCodes.push(action.payload.typeCode);
          }
        }
      })
      .addCase(fetchCodeTableByType.rejected, (state, action) => {
        const typeCode = action.meta.arg;
        state.loadingTypeCodes = state.loadingTypeCodes.filter(t => t !== typeCode);
        state.error = action.payload as string || action.error.message || 'Unknown error';
      });
  },
});

export const { clearCodeTables } = codeTableSlice.actions;
export default codeTableSlice.reducer;
